import { RespondToViewport, WithProfiler } from '@dna/mixins'
import styles from '@dna/styles'
import { LitElement, createRef, css, html, ref } from '@lit'

if (!customElements.get('dna-store-map')) {
  class DNAStoreMap extends WithProfiler(RespondToViewport(LitElement)) {
    static properties = {
      context: {
        type: String,
        attribute: 'context',
      },
      current_location: {
        type: Object,
      },
      locations: {
        type: Array,
      },
      map: {
        type: Object,
      },
      markers: {
        type: Array,
      },
    }

    get mapId() {
      return this.context === 'mobile'
        ? 'storeSearchMapMobile'
        : 'storeSearchMapDesktop'
    }

    constructor() {
      super()

      this._setMobileBreakpoints(['mobile', 'tablet'])
      this.mapRequested = false
      this.$map = createRef()
      this.storePinIcon = this._createMapPin('store')
      this.retailerPinIcon = this._createMapPin('retailer')
      this.infoWindow = null
      ;(async () => {
        const { Map: GoogleMap, InfoWindow } =
          await google.maps.importLibrary('maps')
        const { AdvancedMarkerElement } =
          await google.maps.importLibrary('marker')
        this.googleAdvancedMarkerElement = AdvancedMarkerElement
        this.googleMap = GoogleMap
        this.googleInfoWindow = InfoWindow
        this.dispatchEvent(
          new CustomEvent('dna:store-locator:map-library-ready', {
            bubbles: true,
          }),
        )
      })()
    }

    connectedCallback() {
      super.connectedCallback()

      this.currentLocation = false
      this.locations = []
      this.markers = []

      // Update
      document.addEventListener(
        'dna:store-locator:list-update',
        this._setLocations,
      )

      if (this.isMobile) {
        document.addEventListener(
          'dna:store-locator:list-view-toggled',
          this._showMapView,
        )
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback()

      document.removeEventListener(
        'dna:store-locator:list-update',
        this._setLocations,
      )
      document.removeEventListener(
        'dna:store-locator:list-view-toggled',
        this._showMapView,
      )
    }

    _setLocations = (event) => {
      this.currentLocation = event.detail.current_location || false
      this.locations = event.detail.stores || []
      if (this.locations.length) {
        this._renderMap()
        if (!this.isMobile) this.classList.add('active')
      } else {
        this.classList.remove('active')
      }
    }

    _showMapView = (event) => {
      if (event.detail.view_type === 'map') {
        this.classList.add('active')
        // Wait until the map is active and has a height, otherwise
        // fitBounds() won't have any effect.
        setTimeout(() => {
          this.map.fitBounds(this.mapBounds)
        }, 250)
      } else {
        this.classList.remove('active')
      }
    }

    _createMapPin(type) {
      const parser = new DOMParser()
      let pinSvgString = ''

      if (type === 'store')
        pinSvgString = `
          <svg width="35" height="49" viewBox="0 0 35 49" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.8 0.574844C8.41456 0.574844 0.799973 7.65866 0.799973 16.3833C0.799973 31.4395 17.8 48.5748 17.8 48.5748C17.8 48.5748 34.8 31.4395 34.8 16.3833C34.8 7.65866 27.1854 0.574844 17.8 0.574844Z" fill="#092DC5"/><path fill-rule="evenodd" clip-rule="evenodd" d="M27.2711 16.8905V10.0034L26.0616 10.0952C24.1349 10.226 22.2369 10.6342 20.4258 11.3074C19.5018 11.6547 18.6186 12.1027 17.792 12.6434C16.9679 12.1039 16.0878 11.656 15.1673 11.3074C13.358 10.638 11.4624 10.2329 9.53839 10.1044L8.32893 10.0126V16.8997C8.22148 22.8317 11.436 27.4231 17.3805 29.829L17.8057 30.0034L18.2287 29.829C24.1639 27.4139 27.3786 22.8225 27.2711 16.8905ZM17.7989 15.6738C18.4985 16.4566 18.942 17.1522 18.942 18.3368C18.942 19.3974 18.3476 19.8818 17.7989 19.884C17.2501 19.8863 16.6557 19.3974 16.6557 18.3368C16.6465 17.1545 17.0969 16.4658 17.7989 15.6738ZM17.7977 27.5466L17.7989 27.5471H17.7966L17.7977 27.5466ZM10.5718 16.9318C10.4826 21.7202 13.0475 25.4803 17.7977 27.5466C22.5456 25.4757 25.1059 21.7202 25.0191 16.9318V16.7665V12.5149C23.6587 12.7353 21.4044 13.2335 19.5753 14.2803C20.643 15.5131 21.194 16.6976 21.194 18.3368C21.194 20.8046 19.4473 22.1453 17.8011 22.1453H17.7897C16.1436 22.1384 14.3968 20.8046 14.3968 18.3368C14.3968 16.6953 14.9501 15.5085 16.0201 14.2757C14.191 13.2288 11.9322 12.7422 10.5718 12.5126V16.7665V16.9318Z" fill="white"/></svg>
        `
      if (type === 'retailer')
        pinSvgString = `
          <svg width="28" height="40" viewBox="0 0 28 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 0.00341797C6.27083 0.00341797 0 5.9066 0 13.1771C0 25.7239 14 40.0034 14 40.0034C14 40.0034 28 25.7239 28 13.1771C28 5.9066 21.7292 0.00341797 14 0.00341797Z" fill="#03143B"/><path d="M14 0.00341797C6.27083 0.00341797 0 5.9066 0 13.1771C0 25.7239 14 40.0034 14 40.0034C14 40.0034 28 25.7239 28 13.1771C28 5.9066 21.7292 0.00341797 14 0.00341797Z" fill="black" fill-opacity="0.2"/><ellipse cx="14" cy="15.1385" rx="4.30769" ry="4.32432" fill="white"/></svg>
        `

      const pinSvg = parser.parseFromString(
        pinSvgString,
        'image/svg+xml',
      ).documentElement

      return pinSvg
    }

    _createMap() {
      if (!this.map) {
        const map = new this.googleMap(this.$map.value, {
          minZoom: 8,
          maxZoom: 13,
          mapId: this.mapId,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
        })
        this.map = map
      }

      // Clear existing markers
      for (const marker of this.markers) {
        marker.setMap(null)
        marker.removeEventListener('click', marker._handleMarkerClick)
      }
      this.markers = []

      const storeInfoWindow = document.createElement('div')

      const storeInfoComponent = document.createElement('dna-store-list')
      storeInfoComponent.hasLocation = false
      storeInfoComponent.isInfoWindow = true

      storeInfoWindow.appendChild(storeInfoComponent)

      const closeButton = document.createElement('div')
      closeButton.classList.add('store__info-window-close')
      closeButton.textContent = '✕'
      storeInfoWindow.appendChild(closeButton)

      const infoWindow = new google.maps.InfoWindow({
        content: storeInfoWindow,
        headerDisabled: true,
        maxWidth: 343,
        minWidth: 343,
      })

      closeButton.addEventListener('click', () => {
        infoWindow.close()
      })
      // Add new markers
      for (const location of this.locations) {
        const position = new google.maps.LatLng(
          location.fields.store_latitude,
          location.fields.store_longitude,
        )
        const pinIcon =
          location.type === 'wholesale_stores'
            ? this.retailerPinIcon.cloneNode(true)
            : this.storePinIcon.cloneNode(true)
        const markerOptions = {
          map: this.map,
          position: position,
          content: pinIcon,
          title: location.fields.name,
        }
        const marker = new this.googleAdvancedMarkerElement(markerOptions)
        this.markers.push(marker)

        marker._handleMarkerClick = (event) => {
          event.stopPropagation()

          storeInfoComponent.locations = [location]
          infoWindow.setContent(storeInfoWindow)
          infoWindow.open({
            anchor: marker,
            map: this.map,
            shouldFocus: false,
          })
          this.map.setCenter(position)
        }
        marker.addEventListener('click', marker._handleMarkerClick)
      }

      this.map.addListener('click', (event) => {
        infoWindow.close()
      })
      // Adjust map bounds to fit all markers
      if (this.markers.length > 0) {
        const bounds = new google.maps.LatLngBounds()
        for (const marker of this.markers) {
          bounds.extend(marker.position)
        }
        this.mapBounds = bounds
        setTimeout(() => {
          this.map.fitBounds(this.mapBounds)
        }, 250)
      } else {
        // When no markers, center map on current location if available
        if (this.currentLocation) {
          this.map.setCenter({
            lat: this.currentLocation.latitude,
            lng: this.currentLocation.longitude,
          })
        }
      }
    }

    _renderMap = () => {
      if (!this.googleMap) {
        this.addEventListener('dna:store-locator:map-library-ready', () => {
          this._createMap()
        })
      } else {
        this._createMap()
      }
    }

    render() {
      return html`
        <div
          id=${this.mapId}
          class="stores-search__map-element"
          ${ref(this.$map)}
          ?active=${this.locations.length}
        ></div>
      `
    }

    static styles = [
      styles.a11y,
      css`
        :host(.stores-search__map[context="mobile"]) {
          height: 480px;
        }
        .stores-search__map-element {
          height: 100%;
          width: 100%;
          opacity: 0;
          transition: opacity .2s ease;
        }
        .stores-search__map-element[active] {
          background-color: var(--dna-color-sandstone);
          opacity: 1;
        }
        @media screen and (min-width: 540px) {
          :host(.stores-search__map[context="mobile"]) {
            aspect-ratio: 1.2 / 1;
            height: unset;
          }
        }
        @media screen and (min-width: 1440px) {
          :host(.stores-search__map[context="desktop"]) {
            aspect-ratio: 1;
            height: unset;
          }
        }
        .gm-style .gm-style-iw-c {
          border-radius: 0px;
          padding-top: 8px !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        @media screen and (min-width: 1024px) {
          .gm-style .gm-style-iw-c {
            max-width: 375px !important;
          }
        }
        .gm-style-iw-chr {
          position: absolute;
          right: 0;
        }
        .gm-style-iw-d {
          overflow: auto !important;
        }
        .store__info-window-close {
          position: absolute;
          top: 8px;
          right: 8px;
          cursor: pointer;
          font-size: 16px;
        }
      `,
    ]
  }
  customElements.define('dna-store-map', DNAStoreMap)
}
